import { Link, useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import ManagerLayout from "../../layouts/ManagerLayout";
import { createStage } from "../../api/projectService";

function AddStages() {
    const navigate = useNavigate();
    const { projectId, disciplineId } = useParams();

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        stageName: "",
        description: "",
    });

    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.stageName.trim()) {
            alert("Stage Name is required");
            return;
        }
        setLoading(true);
        try {
            await createStage(projectId, disciplineId, {
                stage_name: formData.stageName.trim(),
                description: formData.description.trim(),
            });

            alert("Stage added successfully!");
            navigate(`/manager/discipline-details/${projectId}`);
        } catch (err) {
            alert(
                err.response?.data?.detail ||
                err.response?.data?.error ||
                "Failed to add stage."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <ManagerLayout>
            <div className="p-3 sm:p-6">
                <div className="w-full max-w-[1400px] mx-auto">

                    {/* ── HEADER ── */}
                    <motion.div
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        className="max-w-3xl mx-auto mb-6 sm:mb-8"
                    >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-3 sm:gap-4">
                                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg flex-shrink-0">
                                    <span className="text-2xl sm:text-3xl">📐</span>
                                </div>
                                <div>
                                    <h1 className="text-xl sm:text-3xl font-bold text-white">
                                        Add Stage
                                    </h1>
                                    <p className="text-white/60 text-xs sm:text-sm">
                                        Add a stage to Discipline {disciplineId} · Project {projectId}
                                    </p>
                                </div>
                            </div>

                            {/* Bulk Upload */}
                            <Link
                                to={`/manager/projects/${projectId}/disciplines/${disciplineId}/stages/bulk`}
                                className="self-start sm:self-auto"
                            >
                                <motion.button
                                    whileHover={{ scale: 1.04 }}
                                    whileTap={{ scale: 0.97 }}
                                    className="flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl border-2 border-cyan-400/60 bg-cyan-500/15 text-cyan-200 font-semibold text-xs sm:text-sm backdrop-blur-sm hover:bg-cyan-500/25 hover:border-cyan-300 transition-all shadow-lg whitespace-nowrap"
                                >
                                    <span className="text-sm sm:text-base">📊</span>
                                    Bulk Upload
                                </motion.button>
                            </Link>
                        </div>
                    </motion.div>

                    {/* ── FORM CARD ── */}
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        className="bg-white/15 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-5 sm:p-8 max-w-3xl mx-auto"
                    >
                        <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">

                            {/* Stage Name */}
                            <div>
                                <label className="block text-white font-semibold mb-2 text-sm">
                                    Stage Name <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="stageName"
                                    required
                                    value={formData.stageName}
                                    onChange={handleChange}
                                    placeholder="Enter stage name (e.g. Architectural, Structural)"
                                    className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white text-sm sm:text-base placeholder-white/40 focus:outline-none focus:border-cyan-400 transition"
                                />
                            </div>



                            {/* Description */}
                            <div>
                                <label className="block text-white font-semibold mb-2 text-sm">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    rows={3}
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Brief description of this stage..."
                                    className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white text-sm sm:text-base placeholder-white/40 focus:outline-none focus:border-cyan-400 transition resize-none"
                                />
                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full sm:w-auto px-8 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold shadow-lg disabled:opacity-60 hover:opacity-90 transition text-sm sm:text-base"
                            >
                                {loading ? "Adding..." : "Add Stage"}
                            </button>
                        </form>
                    </motion.div>

                    {/* ── BACK ── */}
                    <div className="max-w-3xl mx-auto mt-4 flex justify-start">
                        <Link
                            to={`/manager/discipline-details/${projectId}`}
                            className="px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl border-2 border-white/40 text-white hover:bg-white/10 text-sm transition"
                        >
                            ← Back to Discipline Details
                        </Link>
                    </div>
                </div>
            </div>
        </ManagerLayout>
    );
}

export default AddStages;
